<template>
    <ModalBase @close="close" :with-content="true" :max-width="'80%'">
        <loader :loading="saving"></loader>
        <main>
            <section class="first-section">
                <div class="plano-info">
                    <h1>{{ currentTrad.title }}</h1>
                    <p>{{ $trad.common.brand }} : {{ plano.brand.name }}</p>
                    <p>{{ $trad.common.planogram }} : {{ plano.name }}</p>
                </div>
                <div class="pdf-name"><label> Name </label> <input type="text" v-model="pdfName" /></div>
            </section>
            <section class="second-section">
                <h2>{{ currentTrad.printQuality }}</h2>
                <RadioInput :label="QUALITIES.low.label" :value="QUALITIES.low.value" :name="'printQuality'" v-model="printQuality"></RadioInput>
                <RadioInput :label="QUALITIES.high.label" :value="QUALITIES.high.value" :name="'printQuality'" v-model="printQuality"></RadioInput>
            </section>
            <section class="third-section">
                <CheckBox v-model="frontPage" v-if="hasAreas" :label="currentTrad.overviewPage"></CheckBox>
                <CheckBox v-model="shadesPage" v-if="hasAreas" :label="currentTrad.shadesPage"></CheckBox>
                <CheckBox v-model="planoInfoLastPage" :label="currentTrad.infoPage"></CheckBox>
            </section>
            <footer>
                <ClientColorButton :disabled="!canExport" @click="exportPdf"> {{ $trad.common.exportPDF }}</ClientColorButton>
            </footer>
        </main>
        <InfoPagePdf ref="InfoPagePdf" v-show="false" v-if="plano" :plano="plano"></InfoPagePdf>
    </ModalBase>
</template>

<script>
import { Context } from "plano_engine";
import ModalBase from "@/components/modal/ModalBase";
import RadioInput from "@/components/ui/RadioInput";
import ClientColorButton from "@/components/ui/ClientColorButton";
import CheckBox from "@/components/ui/CheckBox";
import exportService from "@/services/export";
import Globals from "@/components/globals";
import { createMixin } from "@/mixins/TryLoadMixin";
import InfoPagePdf from "./InfoPagePdf";
import loader from "@/components/loader/loader";
import shadeService from "@/services/shades";
import componentService from "@/services/component";
import {
    getAllModuleIdFromModuleElements,
    getAllShadeIdForModuleElements,
    findAllComponentsSpecByType,
    formatSvgForPrint,
    moduleListTemplateForAnArea,
    COMPONENT_TYPE,
    QUALITIES
} from "@/plano/utils";

export default {
    data: () => ({
        pdfName: undefined,
        printQuality: undefined,
        frontPage: true,
        shadesPage: true,
        pdfAreas: [],
        planoInfoLastPage: true
    }),
    props: {
        plano: {
            type: Object
        }
    },
    mixins: [createMixin("saving", "trySave")],
    components: {
        ModalBase,
        RadioInput,
        CheckBox,
        ClientColorButton,
        InfoPagePdf,
        loader
    },
    methods: {
        close() {
            this.$emit("close");
        },
        initValue() {
            this.pdfAreas = Context.generateSvgForPdf();
            this.pdfName = this.plano.name;
            this.printQuality = this.QUALITIES.high.value;
        },
        fetchModuleDetailsAndShades(moduleElements) {
            const moduleIds = getAllModuleIdFromModuleElements(moduleElements);
            const shadeIds = getAllShadeIdForModuleElements(moduleElements);

            return Promise.all([
                componentService.fromIds([...moduleIds], {
                    brandId: this.plano.brand.id,
                    zoneId: this.plano.zone.id,
                    campaignId: this.plano.campaign.id
                }),
                shadeService.fromIds([...shadeIds], {
                    brandId: this.plano.brand.id,
                    zoneId: this.plano.zone.id,
                    campaignId: this.plano.campaign.id
                })
            ]);
        },
        exportPdf() {
            const res = this.trySave(async () => {
                const moduleSpecs = findAllComponentsSpecByType(this.plano.config, [COMPONENT_TYPE.module]); // TODO => filter
                const [modulesDetails, shades] = await this.fetchModuleDetailsAndShades(moduleSpecs);
                let pages = [];
                if (this.frontPage || !this.hasAreas) {
                    pages.push({
                        content: formatSvgForPrint(Context.table.paper.root.node),
                        campaign: this.plano.campaign.name,
                        pager: this.currentTrad.overviewPagePager,
                        center: true
                    });
                }
                if (this.hasAreas) {
                    for (let area of this.pdfAreas) {
                        let final = { height: 800, width: 800 };
                        let scaleFactor = area.width >= area.height ? final.width / area.width : final.height / area.height;
                        let svgToPrint = `<svg height="${final.height}" width="${
                            final.width
                        }"><g stroke="#ddd" stroke-width="2px" transform="scale(${scaleFactor})">${area.svg.outerHTML}</g></svg>`;
                        pages.push({
                            content: svgToPrint,
                            center: true,
                            campaign: this.plano.campaign.name,
                            pager: this.plano.zone.name
                        });

                        if (this.shadesPage) {
                            if (area.module.length > 0) {
                                pages.push({
                                    content: moduleListTemplateForAnArea.call(this, area, modulesDetails, shades),
                                    campaign: this.plano.campaign.name,
                                    pager: this.plano.zone.name
                                });
                            }
                        }
                    }
                }
                if (this.planoInfoLastPage) {
                    pages.push({
                        content: this.$refs.InfoPagePdf.$el.innerHTML,
                        pager: this.currentTrad.planoInfoTitle,
                        campaign: this.plano.campaign.name
                    });
                }
                const url = await exportService.multiPdf({
                    fileName: this.pdfName,
                    name: this.plano.name,
                    planoId: this.plano.id,
                    brandId: this.plano.brand.id,
                    zoneName: this.plano.zone.name,
                    zoneId: this.plano.zone.id,
                    campaignName: this.plano.campaign.name,
                    campaignId: this.plano.campaign.id,
                    compressLevel: this.printQuality,
                    pages
                });
                window.open(`${Globals.getBaseUrl()}${decodeURIComponent(url)}`, "_blank");
            });
        }
    },
    computed: {
        canExport() {
            return this.pdfName && this.printQuality;
        },
        currentTrad() {
            return this.$trad.plano.editor.exportPdfModal;
        },
        hasAreas() {
            return this.pdfAreas && Array.isArray(this.pdfAreas) && this.pdfAreas.length > 0;
        },
        QUALITIES() {
            return {
                low: {
                    label: this.currentTrad.lowQuality,
                    value: QUALITIES.low
                },
                high: {
                    label: this.currentTrad.highQuality,
                    value: QUALITIES.high
                }
            };
        }
    },
    created() {
        this.initValue();
    }
};
</script>

<style scoped lang="scss">
@import "../../../assets/css/themes";

main {
    min-width: 600px;
    font-size: 12px;
    section {
        display: flex;
        justify-content: space-between;
        margin: 30px 0px;

        &.first-section {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            margin: auto;
            .plano-info {
                p {
                    margin: 3px 0px 0px 0px;
                    text-transform: uppercase;
                    font-size: 12px !important;
                }
            }
            .pdf-name {
                label {
                    font-size: 12px;
                    display: block;
                    text-transform: uppercase;
                }
                input {
                    padding: 4px;
                }
            }
        }

        &.second-section {
            div {
                flex-grow: 2;
            }
            h2 {
                text-transform: uppercase;
                color: $color2;
                flex-grow: 1;
                font-family: "Oswald", Sans-Serif;
            }
        }
        &.third-section {
            max-width: 300px;
            flex-direction: column;
            justify-content: flex-start;
        }
    }
    footer {
        text-align: right;
    }
}
</style>



// WEBPACK FOOTER //
// index.vue?2e4f61cc